import type { Story } from './types';
// Type-only, so this module stays free of a runtime dependency on permissions.ts.
import type { Actor } from './permissions';

/**
 * Who may change a story, and why — the one place that answers it.
 *
 *   'own'     — you recorded it. It is yours: edit it, retitle it, delete it.
 *   'admin'   — you may only because Admin mode is on. Painted in the admin
 *               colour, like every other power ordinary members do not have.
 *   'suggest' — everybody else. You can say what you think it should be; the
 *               person whose story it is decides.
 *
 * Deliberately narrower than the old rule, which also let anyone managing the
 * storyteller's circle edit the story. A story is the record of one person
 * sitting down and telling it, and it stays with whoever did that.
 */
export type StoryEditRight = 'own' | 'admin' | 'suggest';

export function storyEditRight(actor: Actor, story: Pick<Story, 'creatorAccountId'>): StoryEditRight {
  if (story.creatorAccountId === actor.session.accountId) return 'own';
  return actor.session.role === 'admin' ? 'admin' : 'suggest';
}

/** May this actor change the story straight away, without asking anyone? */
export function mayEditStory(actor: Actor, story: Pick<Story, 'creatorAccountId'>): boolean {
  return storyEditRight(actor, story) !== 'suggest';
}

/**
 * Who answers a suggestion. The person whose story it is, first — and an
 * administrator, so a suggestion is never stuck behind someone who has stopped
 * using the app.
 */
export function mayDecideStorySuggestions(actor: Actor, story: Pick<Story, 'creatorAccountId'>): boolean {
  return mayEditStory(actor, story);
}

/** Only the person who made a suggestion may take it back. */
export function mayWithdrawSuggestion(actor: Actor, suggestedByAccountId: string): boolean {
  return suggestedByAccountId === actor.session.accountId;
}
